/*
 * Confetti assets are fetched the first time a burst runs, never on page load,
 * so a shopper who never adds to cart never pays for them. Both promises are
 * cached at module scope so overlapping add to carts share a single request.
 */
let confettiPlayerPromise = null;
let confettiDataPromise = null;

// The player is a plain UMD bundle, so it loads through a script tag rather
// than an import. This builds no component markup -- Liquid still owns that.
const loadConfettiPlayer = (src) => {
  if (window.lottie) return Promise.resolve(window.lottie);

  confettiPlayerPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.addEventListener('load', () => resolve(window.lottie), { once: true });
    script.addEventListener(
      'error',
      () => {
        // Cleared so a later add to cart can retry instead of reusing a
        // permanently rejected promise.
        confettiPlayerPromise = null;
        reject(new Error('Confetti player failed to load'));
      },
      { once: true }
    );
    document.head.appendChild(script);
  });

  return confettiPlayerPromise;
};

const loadConfettiData = (src) => {
  confettiDataPromise ??= (async () => {
    const response = await fetch(src);
    if (!response.ok) throw new Error(`Confetti data error: ${response.status}`);
    return response.json();
  })();

  return confettiDataPromise;
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Bound once here rather than in connectedCallback, so the reference stays
    // stable and disconnectedCallback can always remove the listener it added.
    this.handleDiscountApplied = this.handleDiscountApplied.bind(this);
  }

  connectedCallback() {
    document.addEventListener('theme:discount:applied', this.handleDiscountApplied);
  }

  disconnectedCallback() {
    document.removeEventListener('theme:discount:applied', this.handleDiscountApplied);
    this.destroyConfetti();
  }

  // cart-discount.js publishes only after it has re-rendered the drawer, so the
  // burst always lands on the markup that is currently on screen.
  handleDiscountApplied() {
    this.burstConfetti();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    // The host carries is-empty too, and the stylesheet keys off the host, not
    // the inner: while it stays the header is hidden, the inner is a centred
    // one-row grid, and the empty-cart blocks keep showing over the lines just
    // added. Nothing re-renders the host either — getSectionsToRender replaces
    // what is inside #CartDrawer — so it has to be cleared here.
    this.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Fires only when a shopper successfully applies a discount code in the
  // drawer -- never on an add to cart, and never when a code is removed.
  async burstConfetti() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const confetti = this.querySelector('[data-cart-confetti]');
    if (!confetti) return;

    const { confettiPlayer, confettiAnimation, confettiSpeed } = confetti.dataset;
    if (!confettiPlayer || !confettiAnimation) return;

    // Decoration only, so a blocked player or a dropped request is swallowed --
    // it must never interrupt the add to cart the shopper actually asked for.
    try {
      const [lottie, animationData] = await Promise.all([
        loadConfettiPlayer(confettiPlayer),
        loadConfettiData(confettiAnimation),
      ]);

      // renderContents replaced this node while the fetch was in flight, so any
      // burst still running belongs to the previous markup and is dropped here.
      this.destroyConfetti();

      const animation = lottie.loadAnimation({
        container: confetti,
        renderer: 'canvas',
        loop: false,
        autoplay: true,
        animationData,
        rendererSettings: { preserveAspectRatio: 'xMidYMid slice', clearCanvas: true },
      });

      // Authored at 60fps over 5s; the drawer plays it whole but twice as fast.
      animation.setSpeed(Number(confettiSpeed) || 1);
      animation.addEventListener('complete', this.destroyConfetti.bind(this));

      this.confettiAnimation = animation;
      confetti.classList.add('is-bursting');
    } catch (error) {
      console.error('Cart confetti failed:', error);
    }
  }

  // Tears down the canvas and its animation loop the moment the burst ends, so
  // an open drawer is never left holding a live player.
  destroyConfetti() {
    this.confettiAnimation?.destroy();
    this.confettiAnimation = null;
    this.querySelector('[data-cart-confetti]')?.classList.remove('is-bursting');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class StyledWithSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleSwatchClick.bind(this));
  }

  handleSwatchClick(event) {
    const btn = event.target.closest('.cart-drawer__sw-btn');
    if (!btn || btn.classList.contains('is-active')) return;

    const { swIndex: index, swHeading: heading } = btn.dataset;

    const activeBtn = this.querySelector('.cart-drawer__sw-btn.is-active');
    if (activeBtn) {
      activeBtn.classList.remove('is-active');
      activeBtn.setAttribute('aria-pressed', 'false');
    }

    btn.classList.add('is-active');
    btn.setAttribute('aria-pressed', 'true');

    if (heading) {
      const headingEl = this.querySelector('.cart-drawer__styled-with-heading');
      if (headingEl) headingEl.textContent = heading;
    }

    const activePanel = this.querySelector('.cart-drawer__styled-with-panel.is-active');
    if (activePanel) activePanel.classList.remove('is-active');

    const nextPanel = this.querySelector(`.cart-drawer__styled-with-panel[data-sw-index="${index}"]`);
    if (nextPanel) nextPanel.classList.add('is-active');
  }
}

if (!customElements.get('styled-with-swatches')) {
  customElements.define('styled-with-swatches', StyledWithSwatches);
}
